import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, addDoc, serverTimestamp, getDocs, query, where, doc, getDoc, updateDoc } from 'firebase/firestore';
import { getDatabase, ref, get, runTransaction, onValue } from 'firebase/database';
import { db, auth, database } from '../firebase';
import vortexDocsService from '../services/vortexDocsService';
import LoadingSpinner from './LoadingSpinner';
import pdfToText from 'react-pdftotext';
import mammoth from 'mammoth';
import '../styles/QuizGenerator.css';
import { getCachedAreas, getCachedTemas, getCachedModelos } from '../services/rtdbCache';



import { useAuth } from '../hooks/useAuth';
import { INSTITUTION_PROMPTS } from '../constants/institutionPrompts';

// --- SISTEMA DE NORMALIZACIÓN UNIFICADO (Sincronizado con Android) ---
const VAULT_NORMALIZATION = {
    HIERARCHY_MAP: {
        "MARINERO": "MRO", "CABO": "CBO", "TERCER_MAESTRE": "3RM",
        "SEGUNDO_MAESTRE": "2DM", "PRIMER_MAESTRE": "1RM",
        "TENIENTE_CORBETA": "TTE.CORB.", "TENIENTE_FRAGATA": "TTE.FRAG.",
        "TENIENTE_NAVIO": "TTE.NAV.", "CAPITAN_CORBETA": "CAP.CORB."
    },
    CORPS_MAP: {
        "Cuerpo General (CG)": "CG",
        "Infantería de Marina (IM)": "IM",
        "Aeronáutica Naval (AN)": "AN",
        "Mecánicos Navales": "MN",
        "Administración e Intendencia Naval": "SAIN",
        "Sanidad Naval": "SAN",
        "Ingenieros de la Armada": "ING",
        "Justicia Naval": "JUS",
        "Servicio de Docencia Naval": "DOC",
        "Trabajo Social": "TS",
        "Músicos Navales": "MUS",
        "Servicios Generales": "SG"
    },
    SPECIALTY_MAP: {
        // Administración e Intendencia Naval
        "Oficinistas": "OFTA",
        "Costurero": "COSTR",
        "Sastre": "SSTR",
        "Chofer": "CHOFR",
        "Lavandero": "LAVDR",
        "Jardinero": "JRDNR",
        "Servicios Generales de alimentacion": "SGA",
        "impresor": "IMPR",
        "Camarero": "CMRR",
        "Cocinero": "COCN",
        "Panadero": "PNDR",
        "Peluquero": "PLQR",

        // Aeronáutica Naval
        "Armamento Aereo": "ARMM_AER",
        "Mecánico de Aviación": "MEC_AVI",
        "Electrónico de Aviación": "ELE_AVI",
        "Operaciones Aereas": "OPE_AER",
        "Controlador de Vuelo": "CTRL_VLO",

        // Cuerpo General
        "C.G.": "CG",
        "Contramaestres": "CONTRAM",
        "Condestables": "CONDEST",
        "Maquinas": "MAQ",
        "Trabajos Submarinos": "TRAB_SUB",
        "Proteccion Portuaria": "PROT_PORT",

        // Infantería de Marina
        "Infantería de Marina": "INFANTERÍA DE MARINA",

        // Ingenieros
        "Informatico": "INF",
        "Radio Operador": "RAD_OP",
        "Mecánico Automotriz": "MEC_AUTO",
        "Hidrografía": "HIDRO",
        "Informática/Computación": "INF_COMP",
        "carpintero ": "CRPNT",
        "Tornero": "TRNR",
        "Soldador": "SLDR",
        "Electromecanico": "E_MEC",
        "Fontanero": "FONT",

        // Justicia Naval
        "Criminalistica": "CRIM",
        "Licenciado en Derecho": "DER",

        // Mecánicos Navales
        "Mecánicos/Motoristas": "MEC_MOT",
        "Electricistas": "ELEC",
        "Frigoríficos": "FRIG",
        "Control de Averías": "CTRL_AV",
        "Electrónicos": "ELECTRON",

        // Sanidad Naval
        "Médicos Cirujanos": "MED_CIR",
        "Cirujanos Dentistas": "CIR_DENT",
        "Enfermería": "ENF",
        "Psicología": "PSIC",
        "Nutrición": "NUTR",
        "Sanidad": "SAN",
        "camillero ": "CAMILL",
        "Servicios Generales de Sanidad": "SGS",
        "Químico": "QUIM",
        "Dentista": "DENT",
        "Radiologo": "RADIO",
        "Laboratorista": "LAB",

        // Docencia
        "Licenciada en Pedagogia": "PEDAG",
        "Licenciada en Educacion Preescolar": "EDUC_PRE",
        "Licenciada en Educacion Primaria": "EDUC_PRIM",

        // Servicios Generales
        "Alimentación": "ALIM",
        "Servicios Generales": "SG",

        // Trabajo Social
        "Servicio de Trabajo Social Naval": "TSN"
    }
};

const translateHierarchy = (h) => {
    if (!h) return "GLOBAL";
    const upper = h.toUpperCase();
    return VAULT_NORMALIZATION.HIERARCHY_MAP[upper] || upper;
};

const translateCorps = (c) => {
    if (!c) return "GLOBAL";
    const trimmed = c.trim();
    return VAULT_NORMALIZATION.CORPS_MAP[trimmed] || VAULT_NORMALIZATION.CORPS_MAP[trimmed.toUpperCase()] || trimmed.toUpperCase();
};

const translateSpecialty = (s) => {
    if (!s) return "GLOBAL";
    const trimmed = s.trim();
    return VAULT_NORMALIZATION.SPECIALTY_MAP[trimmed] || VAULT_NORMALIZATION.SPECIALTY_MAP[trimmed.toUpperCase()] || trimmed.toUpperCase();
};

const getRTDBPrefix = (inst) => {
    return (!inst || inst === 'SEMAR') ? '' : `content/${inst.toLowerCase()}`;
};

const QuizGenerator = () => {
    const navigate = useNavigate();
    const { user, profileData } = useAuth();
    const [areas, setAreas] = useState([]);
    const [temas, setTemas] = useState([]);
    const [selectedAreas, setSelectedAreas] = useState([]);
    const [selectedTemas, setSelectedTemas] = useState([]);
    const [quizName, setQuizName] = useState('');
    const [numQuestions, setNumQuestions] = useState(10);
    const [difficulty, setDifficulty] = useState('medium');
    const [selectedAiModel, setSelectedAiModel] = useState('');
    const [aiModels, setAiModels] = useState({});
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [loading, setLoading] = useState(true);
    const [generating, setGenerating] = useState(false);
    const [generatedQuizId, setGeneratedQuizId] = useState(null);
    const [userQuizzes, setUserQuizzes] = useState([]);
    const [generationStep, setGenerationStep] = useState('');
    const [generationProgress, setGenerationProgress] = useState(0);
    const [saveProgress, setSaveProgress] = useState(0);
    const [publishProgress, setPublishProgress] = useState(0);

    // VortexDocs States
    const [mode, setMode] = useState('document'); // 'document', 'database', or 'json'
    const [file, setFile] = useState(null);
    const [extractedText, setExtractedText] = useState('');
    const [isProcessing, setIsProcessing] = useState(false);
    const [docAreaId, setDocAreaId] = useState('');
    const [docTemaId, setDocTemaId] = useState('');
    const [customTemaName, setCustomTemaName] = useState('');
    const [generatedQuestions, setGeneratedQuestions] = useState([]);
    const [isVaultPublishing, setIsVaultPublishing] = useState(false);
    const [isReviewSubmitting, setIsReviewSubmitting] = useState(false);
    const [reviewProgress, setReviewProgress] = useState(0);

    // JSON Import States
    const [jsonFile, setJsonFile] = useState(null);
    const [jsonParsedQuiz, setJsonParsedQuiz] = useState(null);
    const [isJsonProcessing, setIsJsonProcessing] = useState(false);
    const [jsonError, setJsonError] = useState('');
    const [jsonSuccess, setJsonSuccess] = useState('');
    const [jsonAreaId, setJsonAreaId] = useState('');
    const [jsonFileContent, setJsonFileContent] = useState('');

    // Token states (licencesPremium RTDB)
    const [userTokensAvailable, setUserTokensAvailable] = useState(0);
    const [userTokensUsed, setUserTokensUsed] = useState(0);
    const [userTokensLeft, setUserTokensLeft] = useState(0);
    const [licenseLoaded, setLicenseLoaded] = useState(false);

    const delay = (ms) => new Promise(res => setTimeout(res, ms));

    const validateQuestions = (questions) => {
        if (!questions || !Array.isArray(questions)) return [];
        return questions.filter(q =>
            q &&
            q.question &&
            Array.isArray(q.options) &&
            q.options.length === 4 &&
            q.options.every(opt => opt && opt.trim() !== '') &&
            q.correctAnswer !== undefined &&
            q.correctAnswer !== null &&
            q.correctAnswer >= 0 &&
            q.correctAnswer <= 3 &&
            q.options[q.correctAnswer] !== undefined
        );
    };

    // Cargar áreas, temas y modelos de IA (con caché)
    useEffect(() => {
        const loadData = async () => {
            try {
                setLoading(true);

                const inst = profileData?.institucion || 'SEMAR';
                const prefix = getRTDBPrefix(inst);

                const paths = {
                    areas: prefix ? `${prefix}/areas` : 'areas',
                    temas: prefix ? `${prefix}/temas` : 'temas',
                    models: 'config/apis/modelos'
                };

                // Cargar áreas desde caché / RTDB
                const areasData = await getCachedAreas();
                if (areasData.length > 0) {
                    setAreas(areasData);
                    console.log('[QuizGenerator] 📦 Áreas cargadas desde caché');
                } else {
                    const areasRef = ref(database, paths.areas);
                    const areasSnapshot = await get(areasRef);
                    if (areasSnapshot.exists()) {
                        setAreas(Object.values(areasSnapshot.val()));
                    }
                }

                // Cargar temas desde caché / RTDB
                const temasData = await getCachedTemas();
                if (temasData.length > 0) {
                    setTemas(temasData);
                    console.log('[QuizGenerator] 📦 Temas cargados desde caché');
                } else {
                    const temasRef = ref(database, paths.temas);
                    const temasSnapshot = await get(temasRef);
                    if (temasSnapshot.exists()) {
                        setTemas(Object.values(temasSnapshot.val()));
                    }
                }

                // Cargar modelos de IA desde caché
                const models = await getCachedModelos();
                if (Object.keys(models).length > 0) {
                    setAiModels(models);
                    setSelectedAiModel(Object.keys(models)[0]);
                    console.log('[QuizGenerator] 📦 Modelos cargados desde caché');
                }

                // Cargar cuestionarios del usuario (Estructura jerárquica)
                if (auth.currentUser) {
                    const userQuizzesRef = collection(db, 'user_quizzes', auth.currentUser.uid, 'quizzes');
                    const userQuizzesSnapshot = await getDocs(userQuizzesRef);
                    const userQuizzesData = userQuizzesSnapshot.docs
                        .map(doc => ({ id: doc.id, ...doc.data() }))
                        .filter(q => !q.deleted); // Filtrar eliminados localmente
                    setUserQuizzes(userQuizzesData);
                }
            } catch (error) {
                console.error('Error loading data:', error);
            } finally {
                setLoading(false);
            }
        };

        loadData();
    }, [user, profileData?.institucion]);

    // ── Suscripción en tiempo real al saldo de tokens del usuario ──
    useEffect(() => {
        if (!auth.currentUser?.email) return;
        const licenseKey = auth.currentUser.uid;
        const licRef = ref(database, `licencesPremium/${licenseKey}`);

                console.log(`[QuizGenerator] 🔄 Suscribiendo a tokens de: ${licenseKey}`);

        const unsub = onValue(licRef, (snap) => {
            if (snap.exists()) {
                const lic = snap.val();
                const available = lic.tokensAvailable ?? 2000000;
                const used = lic.tokensUsed ?? 0;
                const left = available - used;
                setUserTokensAvailable(available);
                setUserTokensUsed(used);
                setUserTokensLeft(left);
                console.log(`[QuizGenerator] 🪙 Tokens: ${left.toLocaleString()} disponibles (Usados: ${used.toLocaleString()} / ${available.toLocaleString()})`);
            } else {
                // Sin licencia registrada: asignar cuota de emergencia mínima
                setUserTokensAvailable(50000);
                setUserTokensUsed(0);
                setUserTokensLeft(50000);
                console.warn(`[QuizGenerator] ⚠️ Sin nodo licencesPremium para ${licenseKey}. Cuota de emergencia activada.`);
            }
            setLicenseLoaded(true);
        });

        return () => unsub();
    }, [auth.currentUser?.email]);

    // Manejar selección de áreas
    const handleAreaToggle = (areaId) => {
        setSelectedAreas(prev =>
            prev.includes(areaId)
                ? prev.filter(id => id !== areaId)
                : [...prev, areaId]
        );

        // Limpiar temas si el área se deselecciona
        if (selectedAreas.includes(areaId)) {
            setSelectedTemas(prev => prev.filter(temaId => {
                const tema = temas.find(t => t.id === temaId);
                return tema && tema.areaId !== areaId;
            }));
        }
    };

    // Manejar selección de temas
    const handleTemaToggle = (temaId) => {
        setSelectedTemas(prev =>
            prev.includes(temaId)
                ? prev.filter(id => id !== temaId)
                : [...prev, temaId]
        );
    };

    // Obtener temas por área
    const getTemasByArea = (areaId) => {
        return temas.filter(tema => tema.areaId === areaId);
    };

    const handleFileSelect = async (selectedFile) => {
        if (!selectedFile) return;
        setFile(selectedFile);
        setIsProcessing(true);
        setExtractedText('');

        try {
            const fileType = selectedFile.name.split('.').pop().toLowerCase();
            let text = '';

            if (fileType === 'pdf') {
                text = await pdfToText(selectedFile);

            } else if (fileType === 'docx') {
                const arrayBuffer = await selectedFile.arrayBuffer();
                const result = await mammoth.extractRawText({ arrayBuffer: arrayBuffer });
                text = result.value;

            } else if (fileType === 'txt') {
                text = await selectedFile.text();
            } else {
                alert('Tipo de archivo no soportado. Usa PDF, DOCX o TXT.');
                setIsProcessing(false);
                return;
            }

            if (!text.trim()) {
                alert('No se pudo extraer texto del archivo. Puede ser una imagen escaneada.');
                setIsProcessing(false);
                return;
            }

            // Limpieza básica de texto (reemplazar saltos de línea excesivos)
            const cleanedText = text.replace(/\n\s*\n/g, '\n\n').trim();
            setExtractedText(cleanedText);
            setIsProcessing(false);

        } catch (error) {
            console.error("Error extracting text:", error);
            alert("Error al leer el archivo. Asegúrate de que no esté corrupto o protegido.");
            setIsProcessing(false);
        }
    };

    /**
     * Verifica si el usuario tiene tokens suficientes.
     * @param {number} estimatedCost - Tokens estimados a consumir
     * @returns {{ ok: boolean, needed: number, left: number }}
     */
    const checkTokenLimit = (estimatedCost) => {
        if (!licenseLoaded) return { ok: false, needed: estimatedCost, left: 0 };
        const left = userTokensLeft;
        return { ok: left >= estimatedCost, needed: estimatedCost, left };
    };

    /**
     * Deduce tokens del nodo licencesPremium del usuario en RTDB.
     * @param {number} amount
     */
    const deductTokens = async (amount) => {
        if (!auth.currentUser?.email || amount <= 0) return;
        try {
            const tokenRef = ref(database, `licencesPremium/${auth.currentUser.uid}/tokensUsed`);
            await runTransaction(tokenRef, (current) => (current || 0) + amount);
            console.log(`[QuizGenerator] 💳 Deducidos ${amount.toLocaleString()} tokens en RTDB`);
        } catch (err) {
            console.warn('[QuizGenerator] ⚠️ Error deduciendo tokens:', err);
        }
    };

    const generateDocumentQuiz = async () => {
        if (!extractedText) return;

        if (!docAreaId || !customTemaName) {
            alert("Por favor selecciona un Área y escribe el nombre del Tema.");
            return;
        }

        const modelConfig = aiModels[selectedAiModel];
        if (!selectedAiModel || !modelConfig) {
            alert("Por favor selecciona un modelo de IA.");
            return;
        }

        const apiKey = modelConfig.apiKey;

        // ── Verificar saldo de tokens ANTES de procesar ──
        const estimatedCost = numQuestions * 1500; // estimación conservadora por pregunta
        const tokenCheck = checkTokenLimit(estimatedCost);
        if (!tokenCheck.ok) {
            setError(
                `⛔ Tokens insuficientes para generar este cuestionario.\n` +
                `Tienes ${tokenCheck.left.toLocaleString()} tokens disponibles.\n` +
                `Esta generación requiere aproximadamente ${estimatedCost.toLocaleString()} tokens (${numQuestions} preguntas × 1,500).\n` +
                `Reduce el número de preguntas o renueva tu suscripción.`
            );
            return;
        }

        setGenerating(true);
        setIsProcessing(true);
        setGenerationStep('Iniciando procesamiento de documento...');
        setGenerationProgress(0);
        setError('');
        setSuccess('');

        console.info(`[VortexDocs] 🚀 Iniciando generación: ${numQuestions} preguntas para el tema "${customTemaName}". Costo estimado: ${estimatedCost.toLocaleString()} tokens`);
        console.info(`[VortexDocs] 🛡️ RESPALDO ACTIVO: Las preguntas se preservarán aunque falle un chunk intermedio.`);

        try {
            const CHUNK_SIZE = 20000;
            const textChunks = [];
            for (let i = 0; i < extractedText.length; i += CHUNK_SIZE) {
                textChunks.push(extractedText.substring(i, i + CHUNK_SIZE));
            }

            const numChunks = textChunks.length;
            let allValidQuestions = [];

            // Distribución proporcional
            const totalTextLength = extractedText.length;
            const questionsPerChunk = textChunks.map(chunk => {
                const proportion = chunk.length / totalTextLength;
                return Math.max(1, Math.floor(numQuestions * proportion));
            });

            // Ajustar total
            let totalAssigned = questionsPerChunk.reduce((a, b) => a + b, 0);
            let diff = numQuestions - totalAssigned;
            if (diff > 0) {
                const sortedIndices = [...Array(numChunks).keys()].sort((a, b) => textChunks[b].length - textChunks[a].length);
                for (let i = 0; i < diff; i++) {
                    questionsPerChunk[sortedIndices[i % numChunks]]++;
                }
            }

            console.log(`[VortexDocs] � Distribución proporcional:`, questionsPerChunk);

            for (let i = 0; i < numChunks; i++) {
                setGenerationStep(`Procesando parte ${i + 1} de ${numChunks}...`);
                setGenerationProgress(Math.round((i / numChunks) * 100));

                const currentMaxTokens = questionsPerChunk[i] <= 10 ? 2000 :
                    questionsPerChunk[i] <= 30 ? 3500 : 4000;

                const questions = await fetchQuestionsFromAI(
                    textChunks[i],
                    questionsPerChunk[i],
                    difficulty,
                    { ...modelConfig, maxTokens: currentMaxTokens },
                    apiKey,
                    allValidQuestions.map(q => q.question)
                );

                const validFromChunk = validateQuestions(questions);
                const uniqueFromChunk = validFromChunk.filter(q => !allValidQuestions.some(aq => aq.question.toLowerCase().trim() === q.question.toLowerCase().trim()));
                allValidQuestions.push(...uniqueFromChunk);

                // ── Deducir tokens inmediatamente tras recibir respuesta de IA ──
                const chunkTokenCost = Math.ceil((textChunks[i].length + (questions ? JSON.stringify(questions).length : 0)) / 4);
                await deductTokens(chunkTokenCost);

                console.log(`[VortexDocs] 📥 Recibidas ${uniqueFromChunk.length} preguntas únicas de la parte ${i + 1}. Total: ${allValidQuestions.length}. Tokens deducidos este chunk: ${chunkTokenCost.toLocaleString()}`);

                if (i < numChunks - 1) await delay(2000);
            }

            // reintentos agresivos si faltan
            let missing = numQuestions - allValidQuestions.length;
            let retries = 0;
            const maxRetries = 10;

            while (missing > 0 && retries < maxRetries) {
                retries++;
                const questionsInRetry = Math.min(missing, 10);

                console.log(`[VortexDocs] ⚠️ Faltan ${missing} preguntas. Reintento ${retries}/${maxRetries} solicitando lote de ${questionsInRetry}...`);
                setGenerationStep(`Reintento ${retries}: Completando ${missing} preguntas faltantes...`);

                const suitableChunks = textChunks.filter(c => c.length > 2000);
                const randomChunk = suitableChunks.length > 0
                    ? suitableChunks[Math.floor(Math.random() * suitableChunks.length)]
                    : textChunks[Math.floor(Math.random() * numChunks)];

                const retryMaxTokens = questionsInRetry <= 5 ? 2000 : 3500;

                const extraQuestions = await fetchQuestionsFromAI(
                    randomChunk,
                    questionsInRetry,
                    difficulty,
                    { ...modelConfig, maxTokens: retryMaxTokens },
                    apiKey,
                    allValidQuestions.map(q => q.question)
                );

                const validFromRetry = validateQuestions(extraQuestions).filter(q =>
                    !allValidQuestions.some(aq => aq.question.toLowerCase().trim() === q.question.toLowerCase().trim())
                );

                if (validFromRetry.length > 0) {
                    allValidQuestions.push(...validFromRetry);
                    console.log(`[VortexDocs] ✅ Recuperadas ${validFromRetry.length} preguntas nuevas en reintento ${retries}.`);
                }

                missing = numQuestions - allValidQuestions.length;
                if (missing > 0) await delay(2000);
            }

            if (allValidQuestions.length === 0) {
                throw new Error("No se pudo generar ninguna pregunta válida. Revisa la configuración del modelo o el texto.");
            }

            setGeneratedQuestions(allValidQuestions);
            const successPercent = Math.round((allValidQuestions.length / numQuestions) * 100);
            if (allValidQuestions.length < numQuestions) {
                setSuccess(`⚠️ Generación parcial: ${allValidQuestions.length} de ${numQuestions} (${successPercent}%)`);
            } else {
                setSuccess(`✅ ¡Éxito! Cuestionario generado con ${allValidQuestions.length} preguntas.`);
            }

            setGenerationProgress(100);
            setGenerationStep('¡Generación finalizada!');

        } catch (err) {
            console.error("[VortexDocs] ❌ Error en generación:", err);
            if (allValidQuestions.length > 0) {
                setGeneratedQuestions(allValidQuestions);
                setSuccess(`⚠️ Generación detenida por error, pero se preservaron ${allValidQuestions.length} preguntas generadas hasta el momento.`);
            } else {
                setError(`Error: ${err.message}`);
            }
        } finally {
            setGenerating(false);
            setIsProcessing(false);
        }
    };

    const handleSaveToAccount = async () => {
        if (!generatedQuestions.length) return;
        setGenerating(true);
        setSaveProgress(0);
        setGenerationStep('Preparando guardado...');
        console.info(`[VortexDocs] 📂 Iniciando guardado en Biblioteca Personal...`);

        try {
            // VALIDACIÓN DE SEGURIDAD: Filtrar cualquier pregunta con campos indefinidos
            const validQuestions = generatedQuestions.filter(q =>
                q.question &&
                Array.isArray(q.options) &&
                q.options.length === 4 &&
                q.correctAnswer !== undefined &&
                q.correctAnswer !== null &&
                q.options[q.correctAnswer] !== undefined
            );

            if (validQuestions.length === 0) {
                throw new Error("No hay preguntas válidas para guardar. Intenta regenerar.");
            }

            if (validQuestions.length < generatedQuestions.length) {
                console.warn(`[VortexDocs] ⚠️ Se descartaron ${generatedQuestions.length - validQuestions.length} preguntas inválidas.`);
            }

            const areaName = areas.find(a => a.id == docAreaId)?.nombre || 'VortexDocs';
            const temaName = customTemaName || temas.find(t => t.id == docTemaId)?.nombre || 'Generado por IA';

            const quizData = {
                name: `Quiz: ${temaName} (${new Date().toLocaleDateString()})`,
                userId: auth.currentUser.uid,
                userName: auth.currentUser.displayName || 'Usuario',
                sourceType: 'document',
                sourceName: file ? file.name : 'Unknown',
                areaId: docAreaId,
                temaId: docTemaId || 999, // ID genérico para personalizados
                temaName: temaName,
                questionCount: generatedQuestions.length,
                difficulty,
                aiModel: selectedAiModel,
                modelName: aiModels[selectedAiModel]?.modelName,
                createdAt: serverTimestamp(),
                lastUsed: null,
                completionCount: 0,
                averageScore: 0
            };

            const userQuizzesRef = collection(db, 'user_quizzes', auth.currentUser.uid, 'quizzes');
            const quizRef = await addDoc(userQuizzesRef, quizData);
            console.log(`[VortexDocs] 📄 Documento de cuestionario creado: ${quizRef.id}`);
            const quizQuestionsRef = collection(db, 'user_quizzes', auth.currentUser.uid, 'quizzes', quizRef.id, 'questions');

            for (let i = 0; i < validQuestions.length; i++) {
                const q = validQuestions[i];
                setGenerationStep(`Guardando: ${i + 1}/${validQuestions.length}`);
                setSaveProgress(Math.round(((i + 1) / validQuestions.length) * 100));

                // TRANSFORMACIÓN A FORMATO DE LA APP (MAPAS)
                const respuestasMap = {};
                q.options.forEach((opt, idx) => {
                    const respId = 1000 + idx; // IDs simulados (1000, 1001...)
                    respuestasMap[respId] = {
                        id: respId,
                        respuesta: opt || 'Opción sin texto',
                        correcta: idx === q.correctAnswer ? 1 : 0,
                        preguntaId: 0
                    };
                });

                await addDoc(quizQuestionsRef, {
                    pregunta: q.question,
                    respuestas: respuestasMap,
                    respuesta_correcta: q.options[q.correctAnswer] || 'Sin respuesta',
                    explicacion: q.explanation || '',
                    dificultad: q.difficulty || difficulty,
                    tema: temaName,
                    area: areaName,
                    addedAt: serverTimestamp()
                });
                if ((i + 1) % 5 === 0) console.log(`[VortexDocs] 📝 Guardadas ${i + 1}/${validQuestions.length} preguntas...`);
            }

            // Tokens ya deducidos por chunk durante la generación (no volver a deducir aquí)

            setGeneratedQuizId(quizRef.id);
            setUserQuizzes(prev => [...prev, { id: quizRef.id, ...quizData }]);
            console.info(`[VortexDocs] ✨ Guardado personal completado exitosamente.`);
            alert('Cuestionario guardado exitosamente en tu biblioteca personal.');
            // NO limpiamos las preguntas para permitir publicación posterior o revisión
        } catch (error) {
            console.error("[VortexDocs] ❌ Error al guardar en cuenta:", error);
            alert(`Error al guardar: ${error.message}`);
        } finally {
            setGenerating(false);
            setGenerationStep('');
            setSaveProgress(0);
        }
    };

    const handleSaveToVault = async () => {
        if (!generatedQuestions.length) return;

        try {
            // El contenido generado se clasifica por EXANI-II y tema, sin perfil militar.
            console.info(`[VortexDocs] 🛡️ Iniciando publicación en Bóveda Técnica...`);
            const userDoc = await getDoc(doc(db, 'users', auth.currentUser.uid));
            if (!userDoc.exists()) {
                alert('Completa tu perfil antes de publicar en la Bóveda.');
                return;
            }

            const themeCode = prompt('Ingresa un código corto para el tema (ej: NAVEGACION, EXCEL, LEYES):', customTemaName?.toUpperCase() || '');
            if (!themeCode) {
                console.warn(`[VortexDocs] ⚠️ Publicación cancelada: No se ingresó código de tema.`);
                return;
            }

            setIsVaultPublishing(true);
            setPublishProgress(0);
            setGenerationStep('Preparando publicación...');

            // VALIDACIÓN DE SEGURIDAD
            const validQuestions = generatedQuestions.filter(q =>
                q.question &&
                Array.isArray(q.options) &&
                q.options.length === 4 &&
                q.correctAnswer !== undefined &&
                q.correctAnswer !== null &&
                q.options[q.correctAnswer] !== undefined
            );

            const standardizedId = `EXANI.${themeCode.toUpperCase().replace(/\s/g, '_')}_${auth.currentUser.displayName?.replace(/\s/g, '_') || 'USUARIO'}`;
            console.log(`[VortexDocs] 🛠️ Generando ID estandarizado: ${standardizedId}`);

            if (!await window.confirmAsync(`¿Publicar en la Bóveda Técnica como "${standardizedId}"?`)) {
                console.warn(`[VortexDocs] ⚠️ Publicación cancelada por el usuario.`);
                setIsVaultPublishing(false);
                return;
            }

            const areaName = areas.find(a => a.id == docAreaId)?.nombre || 'VortexDocs';
            const temaName = customTemaName || 'Generado por IA';

            // 2. Guardar en shared_vault/{uid}/quizzes/{quizId}
            const vaultData = {
                standardizedId,
                areaId: docAreaId,
                themeCode: themeCode.toUpperCase(),
                authorName: auth.currentUser.displayName || 'Usuario',
                authorId: auth.currentUser.uid,
                name: `Quiz: ${temaName}`,
                questionCount: validQuestions.length,
                difficulty,
                timestamp: serverTimestamp(),
                isVault: true
            };

            const userVaultRef = collection(db, 'shared_vault', auth.currentUser.uid, 'quizzes');
            const vaultRef = await addDoc(userVaultRef, vaultData);

            console.log(`[VortexDocs] 🏰 Entrada en Bóveda creada: ${vaultRef.id}`);
            const vaultQuestionsRef = collection(db, 'shared_vault', auth.currentUser.uid, 'quizzes', vaultRef.id, 'questions');

            for (let i = 0; i < validQuestions.length; i++) {
                const q = validQuestions[i];
                setGenerationStep(`Publicando: ${i + 1}/${validQuestions.length}`);
                setPublishProgress(Math.round(((i + 1) / validQuestions.length) * 100));

                // TRANSFORMACIÓN A FORMATO DE LA APP (MAPAS)
                const respuestasMap = {};
                q.options.forEach((opt, idx) => {
                    const respId = 1000 + idx;
                    respuestasMap[respId] = {
                        id: respId,
                        respuesta: opt || 'Opción sin texto',
                        correcta: idx === q.correctAnswer ? 1 : 0,
                        preguntaId: 0
                    };
                });

                await addDoc(vaultQuestionsRef, {
                    pregunta: q.question,
                    respuestas: respuestasMap, // Mapa
                    respuesta_correcta: q.options[q.correctAnswer] || 'Sin respuesta',
                    explicacion: q.explanation || '',
                    dificultad: q.difficulty || difficulty,
                    tema: temaName,
                    area: areaName
                });
                if ((i + 1) % 5 === 0) console.log(`[VortexDocs] 🚢 Publicadas ${i + 1}/${validQuestions.length} preguntas en Bóveda...`);
            }

            console.info(`[VortexDocs] 🏆 Publicación exitosa en Bóveda Técnica.`);
            alert('¡Publicado exitosamente! Ahora es visible para los estudiantes en la Web y Android.');
            // No reseteamos automáticamente para permitir guardar en cuenta si se desea
        } catch (error) {
            console.error("[VortexDocs] ❌ Error al publicar en Bóveda:", error);
            alert(`Error al publicar: ${error.message}`);
        } finally {
            setIsVaultPublishing(false);
            setGenerationStep('');
            setPublishProgress(0);
        }
    };

    const handleSendToReview = async () => {
        if (!generatedQuestions.length) return;
        setIsReviewSubmitting(true);
        setReviewProgress(0);
        setGenerationStep('Preparando envío a revisión...');

        try {
            const validQuestions = generatedQuestions.filter(q =>
                q.question &&
                Array.isArray(q.options) &&
                q.options.length === 4 &&
                q.correctAnswer !== undefined &&
                q.correctAnswer !== null &&
                q.options[q.correctAnswer] !== undefined
            );

            if (validQuestions.length === 0) {
                throw new Error("No hay preguntas válidas para enviar a revisión.");
            }

            const areaName = areas.find(a => a.id == docAreaId)?.nombre || 'VortexDocs';
            const temaName = customTemaName || 'Generado por IA';

            const reviewData = {
                name: `Revisión: ${temaName} (${new Date().toLocaleDateString()})`,
                userId: auth.currentUser.uid,
                userName: auth.currentUser.displayName || 'Usuario',
                userEmail: auth.currentUser.email || '',
                sourceType: 'document',
                sourceName: file ? file.name : 'Unknown',
                areaId: docAreaId,
                temaId: docTemaId || 999,
                temaName: temaName,
                questionCount: validQuestions.length,
                difficulty,
                aiModel: selectedAiModel,
                modelName: aiModels[selectedAiModel]?.modelName || 'desconocido',
                status: 'pending',
                createdAt: serverTimestamp()
            };

            const reviewRef = await addDoc(collection(db, 'quiz_reviews'), reviewData);
            const reviewQuestionsRef = collection(db, 'quiz_reviews', reviewRef.id, 'questions');

            for (let i = 0; i < validQuestions.length; i++) {
                const q = validQuestions[i];
                setGenerationStep(`Enviando: ${i + 1}/${validQuestions.length}`);
                setReviewProgress(Math.round(((i + 1) / validQuestions.length) * 100));

                const respuestasMap = {};
                q.options.forEach((opt, idx) => {
                    const respId = 1000 + idx;
                    respuestasMap[respId] = {
                        id: respId,
                        respuesta: opt || 'Opción sin texto',
                        correcta: idx === q.correctAnswer ? 1 : 0,
                        preguntaId: 0
                    };
                });

                await addDoc(reviewQuestionsRef, {
                    pregunta: q.question,
                    respuestas: respuestasMap,
                    respuesta_correcta: q.options[q.correctAnswer] || 'Sin respuesta',
                    explicacion: q.explanation || '',
                    dificultad: q.difficulty || difficulty,
                    tema: temaName,
                    area: areaName,
                    addedAt: serverTimestamp()
                });
            }

            alert('¡Propuesta enviada exitosamente para revisión del administrador!');
        } catch (error) {
            console.error("Error al enviar a revisión:", error);
            alert(`Error al enviar a revisión: ${error.message}`);
        } finally {
            setIsReviewSubmitting(false);
            setGenerationStep('');
            setReviewProgress(0);
        }
    };

    const handleJsonFileSelect = async (selectedFile) => {
        if (!selectedFile) return;
        setJsonFile(selectedFile);
        setIsJsonProcessing(true);
        setJsonError('');
        setJsonSuccess('');
        setJsonParsedQuiz(null);
        setJsonFileContent('');

        try {
            let text = await selectedFile.text();
            setJsonFileContent(text);
            
            // Limpiar bloques de markdown si el archivo fue copiado directamente de un chat de IA
            text = text.trim();
            if (text.startsWith('```')) {
                text = text.replace(/^```json/i, '').replace(/^```/i, '');
                text = text.replace(/```$/i, '').trim();
            }

            let parsed;
            try {
                parsed = JSON.parse(text);
            } catch (e) {
                console.error("Detalle del error de parseo JSON:", e, "Texto recibido:", text.substring(0, 200) + "...");
                throw new Error(`El archivo no contiene un formato JSON válido. Detalle: ${e.message}`);
            }

            let quizName = parsed.name || parsed.titulo || `Quiz Importado (${new Date().toLocaleDateString()})`;
            let questionsRaw = Array.isArray(parsed) ? parsed : (parsed.questions || parsed.preguntas || parsed.cuestionario || []);
            
            if (!Array.isArray(questionsRaw)) {
                if (typeof questionsRaw === 'object' && questionsRaw !== null) {
                    questionsRaw = [questionsRaw];
                } else {
                    throw new Error("No se encontró un listado de preguntas válido en el JSON.");
                }
            }

            const parsedQuestions = questionsRaw.map((q, idx) => {
                const questionText = q.question || q.pregunta || q.text || '';
                const rawOptions = q.options || q.opciones || q.respuestas || [];
                let explanation = q.explanation || q.explicacion || '';
                let difficulty = q.difficulty || q.dificultad || 'medium';

                let options = [];
                let correctAnswer = 0;

                if (Array.isArray(rawOptions)) {
                    options = rawOptions.map(opt => {
                        if (typeof opt === 'object' && opt !== null) {
                            return opt.respuesta || opt.texto || opt.option || String(opt);
                        }
                        return String(opt);
                    });

                    // Buscar si las opciones en el array son objetos y una tiene la bandera correcta
                    const correctObjectIdx = rawOptions.findIndex(opt => typeof opt === 'object' && opt !== null && (opt.correcta === true || opt.correcta === 1 || opt.isCorrect === true));
                    
                    if (correctObjectIdx !== -1) {
                        correctAnswer = correctObjectIdx;
                    } else {
                        const correctVal = q.correctAnswer !== undefined ? q.correctAnswer : 
                                          (q.respuesta_correcta !== undefined ? q.respuesta_correcta : 
                                          (q.correct_index !== undefined ? q.correct_index : 0));
                        
                        if (typeof correctVal === 'number') {
                            correctAnswer = correctVal;
                        } else if (typeof correctVal === 'string') {
                            const parsedIdx = parseInt(correctVal);
                            if (!isNaN(parsedIdx)) {
                                correctAnswer = parsedIdx;
                            } else {
                                correctAnswer = options.findIndex(opt => opt === correctVal);
                                if (correctAnswer === -1) correctAnswer = 0;
                            }
                        }
                    }
                } else if (typeof rawOptions === 'object' && rawOptions !== null) {
                    const sortedOptions = Object.values(rawOptions).sort((a, b) => (a.id || 0) - (b.id || 0));
                    options = sortedOptions.map(o => o.respuesta || o.texto || o.option || '');
                    const correctIdx = sortedOptions.findIndex(o => o.correcta === 1 || o.correcta === true || o.isCorrect === true);
                    correctAnswer = correctIdx !== -1 ? correctIdx : 0;
                }

                return {
                    id: idx + 1,
                    question: questionText,
                    options: options.slice(0, 4),
                    correctAnswer: correctAnswer >= 0 && correctAnswer <= 3 ? correctAnswer : 0,
                    explanation,
                    difficulty
                };
            }).filter(q => q.question && q.options.length === 4);

            if (parsedQuestions.length === 0) {
                throw new Error("No se encontraron preguntas legibles con 4 opciones cada una.");
            }

            setJsonParsedQuiz({
                name: quizName,
                questions: parsedQuestions
            });
            setJsonSuccess(`¡JSON leído correctamente! Cuestionario: "${quizName}" con ${parsedQuestions.length} preguntas.`);
        } catch (err) {
            console.error("Error al procesar JSON:", err);
            setJsonError(err.message);
        } finally {
            setIsJsonProcessing(false);
        }
    };

    const handleSaveJsonQuiz = async () => {
        if (!jsonParsedQuiz) return;
        setIsJsonProcessing(true);
        setJsonError('');
        setJsonSuccess('');

        try {
            const areaName = areas.find(a => a.id == jsonAreaId)?.nombre || 'Importados';
            const quizData = {
                name: jsonParsedQuiz.name,
                userId: auth.currentUser.uid,
                userName: auth.currentUser.displayName || 'Usuario',
                sourceType: 'json_import',
                sourceName: jsonFile ? jsonFile.name : 'Imported.json',
                areaId: jsonAreaId || '3',
                temaId: 999,
                temaName: jsonParsedQuiz.name,
                questionCount: jsonParsedQuiz.questions.length,
                difficulty: 'medium',
                createdAt: serverTimestamp(),
                lastUsed: null,
                completionCount: 0,
                averageScore: 0
            };

            const userQuizzesRef = collection(db, 'user_quizzes', auth.currentUser.uid, 'quizzes');
            const quizRef = await addDoc(userQuizzesRef, quizData);

            const quizQuestionsRef = collection(db, 'user_quizzes', auth.currentUser.uid, 'quizzes', quizRef.id, 'questions');
            for (let i = 0; i < jsonParsedQuiz.questions.length; i++) {
                const q = jsonParsedQuiz.questions[i];
                const respuestasMap = {};
                q.options.forEach((opt, idx) => {
                    const respId = 1000 + idx;
                    respuestasMap[respId] = {
                        id: respId,
                        respuesta: opt,
                        correcta: idx === q.correctAnswer ? 1 : 0,
                        preguntaId: 0
                    };
                });

                await addDoc(quizQuestionsRef, {
                    pregunta: q.question,
                    respuestas: respuestasMap,
                    respuesta_correcta: q.options[q.correctAnswer] || 'Sin respuesta',
                    explicacion: q.explanation || '',
                    dificultad: q.difficulty || 'medium',
                    tema: jsonParsedQuiz.name,
                    area: areaName,
                    addedAt: serverTimestamp()
                });
            }

            setJsonSuccess(`¡Cuestionario "${jsonParsedQuiz.name}" guardado exitosamente en tus cuestionarios personales!`);
            setJsonFile(null);
            setJsonParsedQuiz(null);
            setJsonAreaId('');
            
            const userQuizzesSnapshot = await getDocs(collection(db, 'user_quizzes', auth.currentUser.uid, 'quizzes'));
            const userQuizzesData = userQuizzesSnapshot.docs
                .map(doc => ({ id: doc.id, ...doc.data() }))
                .filter(q => !q.deleted);
            setUserQuizzes(userQuizzesData);

        } catch (error) {
            console.error("Error al guardar quiz JSON:", error);
            setJsonError(`Error al guardar: ${error.message}`);
        } finally {
            setIsJsonProcessing(false);
        }
    };

    const fetchQuestionsFromAI = async (text, count, diff, modelConfig, apiKey, previousQuestions = []) => {
        const inst = profileData?.institucion || 'SEMAR';
        const institutionPrompt = INSTITUTION_PROMPTS[inst] || INSTITUTION_PROMPTS['SEMAR'];
        return await vortexDocsService.generateQuestions(
            text, count, diff,
            modelConfig.modelKey || selectedAiModel,
            auth.currentUser?.uid,
            previousQuestions,
            institutionPrompt
        );
    };

    // Generar cuestionario personalizado
    const generateCustomQuiz = async () => {
        if (!quizName.trim() || selectedTemas.length === 0) {
            alert('Por favor completa todos los campos');
            return;
        }

        try {
            setGenerating(true);

            // Obtener preguntas de los temas seleccionados
            const questionsQuery = query(
                collection(db, 'preguntas'),
                where('tema_id', 'in', selectedTemas)
            );
            const questionsSnapshot = await getDocs(questionsQuery);
            const allQuestions = questionsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

            // Filtrar preguntas por dificultad si es necesario
            const filteredQuestions = allQuestions.filter(question => {
                if (difficulty === 'all') return true;
                return question.dificultad === difficulty;
            });

            // Seleccionar preguntas aleatorias
            const selectedQuestions = filteredQuestions
                .sort(() => Math.random() - 0.5)
                .slice(0, Math.min(numQuestions, filteredQuestions.length));

            // Crear el cuestionario personalizado
            const quizData = {
                name: quizName,
                userId: auth.currentUser.uid,
                userName: auth.currentUser.displayName || 'Usuario',
                areas: selectedAreas,
                temas: selectedTemas,
                questionCount: selectedQuestions.length,
                difficulty,
                aiModel: selectedAiModel,
                modelName: aiModels[selectedAiModel]?.modelName || 'desconocido',
                createdAt: serverTimestamp(),
                lastUsed: null,
                completionCount: 0,
                averageScore: 0
            };

            const userQuizzesRef = collection(db, 'user_quizzes', auth.currentUser.uid, 'quizzes');
            const quizRef = await addDoc(userQuizzesRef, quizData);

            // Guardar las preguntas seleccionadas
            const quizQuestionsRef = collection(db, 'user_quizzes', auth.currentUser.uid, 'quizzes', quizRef.id, 'questions');
            for (const question of selectedQuestions) {
                await addDoc(quizQuestionsRef, {
                    ...question,
                    addedAt: serverTimestamp()
                });
            }

            setGeneratedQuizId(quizRef.id);
            setUserQuizzes(prev => [...prev, { id: quizRef.id, ...quizData }]);

            alert('Cuestionario generado con éxito!');
        } catch (error) {
            console.error('Error generating quiz:', error);
            alert('Error al generar el cuestionario');
        } finally {
            setGenerating(false);
        }
    };

    // Eliminar cuestionario
    const deleteQuiz = async (quizId) => {
        if (!await window.confirmAsync('¿Estás seguro de que quieres eliminar este cuestionario?')) return;

        try {
            const quizDocRef = doc(db, 'user_quizzes', auth.currentUser.uid, 'quizzes', quizId);
            await updateDoc(quizDocRef, {
                deleted: true,
                deletedAt: serverTimestamp()
            });
            setUserQuizzes(prev => prev.filter(quiz => quiz.id !== quizId));
            alert('Cuestionario eliminado');
        } catch (error) {
            console.error('Error deleting quiz:', error);
            alert('Error al eliminar el cuestionario');
        }
    };

    // Iniciar cuestionario
    const startQuiz = (quizId) => {
        navigate(`/dashboard/quiz-generator/${quizId}`);
    };

    // Publicar cuestionario a la Bóveda Técnica
    const publishToVault = async (quiz) => {
        try {
            // 1. Obtener perfil del usuario para códigos
            const userDoc = await getDoc(doc(db, 'users', auth.currentUser.uid));
            if (!userDoc.exists()) {
                alert('Debes completar tu perfil con Jerarquía y Especialidad antes de publicar.');
                return;
            }

            const userData = userDoc.data();
            const grado = userData.grado || userData.hierarchy || userData.jerarquia;
            const cuerpo = userData.cuerpoServicio || userData.corps || userData.cuerpo;
            const especialidad = userData.especialidad || userData.specialty;

            if (!grado || !cuerpo || !especialidad) {
                alert('⚠️ Perfil incompleto. Configura tu Grado, Cuerpo y Especialidad en tu Perfil antes de publicar.');
                return;
            }

            const hierarchyCode = translateHierarchy(grado);
            const corpsCode = translateCorps(cuerpo);
            const specialtyCode = translateSpecialty(especialidad);

            const themeInput = prompt('Ingresa el código del tema (ej: EXCEL2026, NAVEGACION, MOTORES):');
            if (!themeInput) return;

            const standardizedId = `${hierarchyCode}.${corpsCode}.${specialtyCode}.${themeInput.toUpperCase()}_${auth.currentUser.displayName?.replace(/\s/g, '_') || 'USUARIO'}`;

            if (!await window.confirmAsync(`¿Publicar como "${standardizedId}"?`)) return;

            // 2. Copiar cuestionario a shared_vault
            const vaultData = {
                standardizedId,
                hierarchyCode,
                corpsCode,
                specialtyCode,
                areaId: quiz.areaId,
                themeCode: themeInput.toUpperCase(),
                authorName: auth.currentUser.displayName || 'Usuario',
                authorId: auth.currentUser.uid,
                name: quiz.name,
                questionCount: quiz.questionCount,
                difficulty: quiz.difficulty,
                timestamp: serverTimestamp()
            };

            const vaultRef = await addDoc(collection(db, 'shared_vault', auth.currentUser.uid, 'quizzes'), vaultData);

            // 3. Copiar preguntas desde la nueva ruta
            const questionsSnapshot = await getDocs(collection(db, 'user_quizzes', auth.currentUser.uid, 'quizzes', quiz.id, 'questions'));
            const vaultQuestionsRef = collection(db, 'shared_vault', auth.currentUser.uid, 'quizzes', vaultRef.id, 'questions');

            for (const qDoc of questionsSnapshot.docs) {
                await addDoc(vaultQuestionsRef, qDoc.data());
            }

            alert('¡Cuestionario publicado en la Bóveda Técnica! Otros marineros de tu especialidad podrán acceder a él.');
        } catch (error) {
            console.error('Error publishing to vault:', error);
            alert('Error al publicar el cuestionario.');
        }
    };

    // Funciones de traducción de códigos (Sincronizadas con la Bóveda Técnica y Robusta)
    const translateHierarchy = (hierarchy) => {
        if (!hierarchy) return 'UNK';
        const normalized = hierarchy.replace(/_/g, ' ').trim().toLowerCase();
        const map = {
            'marinero': 'MRO',
            'cabo': 'CABO',
            'tercer maestre': '3ER.MTRE',
            'segundo maestre': '2DO.MTRE',
            'primer maestre': '1RM',
            'teniente de corbeta': 'TTE.CORB',
            'teniente de fragata': 'TTE.FRAG',
            'teniente de navío': 'TTE.NAV',
            'capitán de corbeta': 'CAP.CORB',
            'teniente': 'TTE', // Retrocompatibilidad
            'capitán': 'CAP'  // Retrocompatibilidad
        };
        return map[normalized] || 'MRO';
    };

    const translateCorps = (corps) => {
        if (!corps) return 'UNK';
        const normalized = corps.trim().toLowerCase();
        const map = {
            'cuerpo general': 'CGEN',
            'infantería de marina': 'IM',
            'infanteria de marina': 'I.M.', // Sin acento
            'aeronáutica naval': 'AN',
            'aeronautica naval': 'A.N.', // Sin acento
            'servicio de administración e intendencia naval': 'SAIN',
            'servicio de intendencia naval': 'SAIN', // Alias corto
            'servicio de intendencia': 'SAIN', // Alias muy corto
            'servicio de sanidad naval': 'SSN',
            'servicio de sanidad': 'SSN', // Alias
            'servicio de ingenieros de la armada': 'SIA.ING',
            'servicio de mantenimiento a aeronaves': 'MAA',
            'servicio de comunicaciones navales': 'SCN',
            'servicio de comunicaciones': 'COMP', // Alias
            'servicio de máquinas navales': 'CG.MAQS',
            'servicio de maquinas navales': 'CG MAQ', // Sin acento
            'servicio de máquinas': 'MAQ', // Alias
            'servicio de maquinas': 'MAQS', // Alias sin acento
            'servicio de informática naval': 'INF',
            'servicio de informatica naval': 'INFO', // Sin acento
            'servicio médico': 'L.MED',
            'servicio medico': 'MED', // Sin acento
            'servicio de justicia naval': 'SJN',
            'servicio de justicia': 'SJN', // Alias
            'servicio de docencia naval': 'SDN' // Alias



        };
        return map[normalized] || 'SAIN';
    };

    const translateSpecialty = (specialty) => {
        if (!specialty) return 'UNK';
        const normalized = specialty.trim().toLowerCase();
        const map = {
            'oficinista': 'OFTA',
            'lavandería': 'LAVD',
            'lavanderia': 'LAVD',
            'cocina': 'COCN',
            'armamento': 'ARMM',
            'navegación': 'NAVG',
            'navegacion': 'NAVG',
            'telecomunicaciones': 'TELE',
            'electrónica': 'ELEC',
            'electronica': 'ELEC',
            'mecánica': 'MECA',
            'mecanica': 'MECA',
            'contramaestre': 'CTMA',
            'condestable': 'COND',
            'chofer': 'CHOF',
            'pelutería': 'PELU',
            'peluteria': 'PELU',
            'carpintería': 'CARP',
            'carpinteria': 'CARP',
            'electricista': 'ELEC',
            'sastrería': 'SAST',
            'sastreria': 'SAST'
        };
        return map[normalized] || normalized.substring(0, 4).toUpperCase();
    };

    if (loading) {
        return (
            <div className="quiz-generator-container">
                <LoadingSpinner label="Cargando datos..." height="300px" />
            </div>
        );
    }

    return (
        <div className="quiz-generator-container" style={{ maxWidth: '100%', margin: 0, height: 'calc(100vh - 60px)', boxSizing: 'border-box', overflowY: 'auto' }}>
            <h1>Generador de Cuestionarios</h1>

            <div className="generator-tabs">
                <button
                    className={`tab-btn ${mode === 'document' ? 'active' : ''}`}
                    onClick={() => setMode('document')}
                >
                    📁 Desde Documentos (VortexDocs)
                </button>
                <button
                    className={`tab-btn ${mode === 'json' ? 'active' : ''}`}
                    onClick={() => setMode('json')}
                >
                    📂 Cargar JSON Personal
                </button>
            </div>

            {mode === 'document' && (
                <div className="vortex-docs-container">
                    <div className="welcome-card">
                        <div className="welcome-content">
                            <h1>📘 Bienvenido a Vortex Docs</h1>
                            <p>Esta herramienta te permite cargar documentos para generar cuestionarios usando modelos de IA.</p>

                            <h3>🔍 Tipos de Archivos Soportados:</h3>
                            <p>- PDF - TXT - DOC Y DOCX</p>

                            <h1>⚠️ Limitaciones Importantes:</h1>
                            <p>- Máximo recomendado: 10 MB</p>
                            <p>- No se aceptan archivos protegidos con contraseña</p>
                            <p>- Solo se procesa texto legible, no imágenes o fotografías</p>
                        </div>
                    </div>

                    <div className="file-upload-section">
                        <div
                            className={`drop-zone ${isProcessing ? 'processing' : ''}`}
                            onDragOver={(e) => e.preventDefault()}
                            onDrop={(e) => {
                                e.preventDefault();
                                const droppedFile = e.dataTransfer.files[0];
                                if (droppedFile) handleFileSelect(droppedFile);
                            }}
                        >
                            <input
                                type="file"
                                id="file-upload"
                                accept=".pdf,.docx,.txt"
                                onChange={(e) => handleFileSelect(e.target.files[0])}
                                style={{ display: 'none' }}
                            />
                            <label htmlFor="file-upload" className="upload-label">
                                {file ? (
                                    <div className="file-info">
                                        <span className="file-icon">📄</span>
                                        <span className="file-name">{file.name}</span>
                                        <span className="change-file">(Clic para cambiar)</span>
                                    </div>
                                ) : (
                                    <div className="upload-placeholder">
                                        <span className="upload-cloud-icon">☁️</span>
                                        <p className="upload-main-text">Arrastra tu archivo aquí o selecciona un tipo:</p>
                                        <div className="upload-type-buttons">
                                            <button
                                                type="button"
                                                className="upload-type-btn pdf"
                                                onClick={(e) => { e.preventDefault(); document.getElementById('file-upload').click(); }}
                                            >
                                                📄 Subir PDF
                                            </button>
                                            <button
                                                type="button"
                                                className="upload-type-btn docx"
                                                onClick={(e) => { e.preventDefault(); document.getElementById('file-upload').click(); }}
                                            >
                                                📝 Subir DOCX
                                            </button>
                                            <button
                                                type="button"
                                                className="upload-type-btn txt"
                                                onClick={(e) => { e.preventDefault(); document.getElementById('file-upload').click(); }}
                                            >
                                                🗒️ Subir TXT
                                            </button>
                                        </div>
                                        <small className="upload-hint">Soporta PDF, DOCX y TXT · Máx. recomendado: 10MB</small>
                                    </div>
                                )}
                            </label>
                        </div>

                        {isProcessing && (
                            <div className="processing-indicator">
                                <div className="spinner"></div>
                                <p>Procesando documento y extrayendo texto legible...</p>
                            </div>
                        )}

                        {file && !isProcessing && (
                            <div className="file-loaded-badge success">
                                <span className="badge-icon">✅</span>
                                <div className="badge-details">
                                    <strong>Documento cargado con éxito:</strong> {file.name} ({(file.size / 1024).toFixed(1)} KB)
                                    <div className="doc-ready-hint">El texto fue extraído correctamente y está listo para generar preguntas.</div>
                                </div>
                            </div>
                        )}

                        {extractedText && (
                            <div className="extracted-text-preview">
                                <h3>Vista Previa del Texto Extraído</h3>
                                <div className="text-content">
                                    {extractedText.substring(0, 500)}...
                                </div>
                                <div className="doc-config-section" style={{ marginTop: '20px', padding: '15px', background: 'rgba(10, 26, 42, 0.8)', borderRadius: '8px', border: '1px solid var(--tech-border)' }}>
                                    <h4 style={{ marginBottom: '10px', color: 'var(--gold-matte)' }}>Configuración de Generación</h4>

                                    <div className="form-group">
                                        <label>Cantidad de Preguntas:</label>
                                        <input
                                            type="number"
                                            value={numQuestions}
                                            onChange={(e) => setNumQuestions(Math.min(400, Math.max(1, parseInt(e.target.value) || 10)))}
                                            min="1" max="400"
                                            style={{ padding: '8px', borderRadius: '4px', border: '1px solid var(--tech-border)', width: '100%', background: 'rgba(0,0,0,0.5)', color: 'white' }}
                                        />
                                    </div>

                                    <div className="form-group">
                                        <label>Área:</label>
                                        <select
                                            value={docAreaId}
                                            onChange={(e) => { setDocAreaId(e.target.value); setDocTemaId(''); }}
                                            style={{ padding: '8px', borderRadius: '4px', border: '1px solid var(--tech-border)', width: '100%', background: 'rgba(0,0,0,0.5)', color: 'white' }}
                                        >
                                            <option value="">Selecciona un Área</option>
                                            {areas.map(area => (
                                                <option key={area.id} value={area.id}>{area.nombre}</option>
                                            ))}
                                        </select>
                                    </div>

                                    {docAreaId && (
                                        <div className="form-group">
                                            <label>Nombre del Tema (Personalizado):</label>
                                            <input
                                                type="text"
                                                placeholder="Ej: Reglamento de Uniformes, Microsoft Word..."
                                                value={customTemaName}
                                                onChange={(e) => setCustomTemaName(e.target.value)}
                                                style={{ padding: '8px', borderRadius: '4px', border: '1px solid var(--tech-border)', width: '100%', background: 'rgba(0,0,0,0.5)', color: 'white' }}
                                            />
                                            <small style={{ color: 'var(--text-muted)' }}>Escribe el nombre del tema para tu cuestionario.</small>
                                        </div>
                                    )}

                                    <div className="form-group">
                                        <label>Modelo de IA:</label>
                                        <select
                                            value={selectedAiModel}
                                            onChange={(e) => setSelectedAiModel(e.target.value)}
                                            className="ai-model-selector"
                                            style={{ padding: '8px', borderRadius: '4px', border: '1px solid var(--tech-border)', width: '100%', background: 'rgba(0,0,0,0.5)', color: 'white' }}
                                        >
                                            {Object.entries(aiModels).map(([key, model]) => (
                                                <option key={key} value={key}>{model.displayName}</option>
                                            ))}
                                        </select>
                                    </div>
                                </div>

                                {/* ── Indicador de Tokens ── */}
                                <div className="token-status-bar">
                                    <div className="token-status-header">
                                        <span className="token-label">🪙 Tokens disponibles</span>
                                        <span className={`token-count ${userTokensLeft < 50000 ? 'token-critical' : userTokensLeft < 200000 ? 'token-low' : 'token-ok'}`}>
                                            {licenseLoaded ? userTokensLeft.toLocaleString() : 'Cargando...'}
                                            {licenseLoaded && ` / ${userTokensAvailable.toLocaleString()}`}
                                        </span>
                                    </div>
                                    <div className="token-bar-bg">
                                        <div
                                            className={`token-bar-fill ${userTokensLeft < 50000 ? 'critical' : userTokensLeft < 200000 ? 'low' : ''}`}
                                            style={{ width: licenseLoaded && userTokensAvailable > 0 ? `${Math.min(100, (userTokensLeft / userTokensAvailable) * 100)}%` : '0%' }}
                                        />
                                    </div>

                                    {licenseLoaded && (
                                        <p className="token-summary-text">
                                            {userTokensLeft <= 0
                                                ? '⛔ No tienes tokens disponibles. Renueva tu suscripción para continuar generando cuestionarios.'
                                                : userTokensLeft < 50000
                                                ? `⚠️ Saldo crítico: te quedan ${userTokensLeft.toLocaleString()} tokens. Solo puedes generar cuestionarios pequeños.`
                                                : userTokensLeft < 200000
                                                ? `🟡 Saldo bajo: ${userTokensLeft.toLocaleString()} tokens disponibles (aprox. ${Math.floor(userTokensLeft / 1500)} preguntas más).`
                                                : `✅ Tienes ${userTokensLeft.toLocaleString()} tokens disponibles — suficiente para aproximadamente ${Math.floor(userTokensLeft / 1500).toLocaleString()} preguntas más.`
                                            }
                                        </p>
                                    )}

                                    {userTokensLeft < numQuestions * 1500 && licenseLoaded && (
                                        <div className="token-warning-msg">
                                            ⛔ Tokens insuficientes. Necesitas ≈{(numQuestions * 1500).toLocaleString()} para generar {numQuestions} preguntas.
                                        </div>
                                    )}
                                </div>

                                {generating && (
                                    <div className="generation-progress-container" style={{ marginTop: '20px' }}>
                                        <div className="progress-status-text">
                                            <span>{generationStep}</span>
                                            <span>{generationProgress}%</span>
                                        </div>
                                        <div className="progress-bar-background">
                                            <div
                                                className="progress-bar-fill"
                                                style={{ width: `${generationProgress}%` }}
                                            ></div>
                                        </div>
                                    </div>
                                )}

                                <button
                                    className="generate-button"
                                    onClick={generateDocumentQuiz}
                                    disabled={generating || !docAreaId || !customTemaName || !licenseLoaded || userTokensLeft < numQuestions * 1500}
                                    title={userTokensLeft < numQuestions * 1500 ? `Tokens insuficientes (${userTokensLeft.toLocaleString()} disponibles)` : ''}
                                    style={{ marginTop: '20px' }}
                                >
                                    {generating ? '⚙️ Generando...' : userTokensLeft < numQuestions * 1500 && licenseLoaded ? '⛔ Tokens Insuficientes' : '🚀 Generar Cuestionario'}
                                </button>

                                {error && (
                                    <div className="status-message error">
                                        ❌ {error}
                                    </div>
                                )}
                                {success && (
                                    <div className="status-message success">
                                        ✅ {success}
                                    </div>
                                )}

                                {generatedQuestions.length > 0 && (
                                    <div className="save-options-section">
                                        <h3>✓ ¡Cuestionario Generado!</h3>
                                        <p>Se han generado <strong>{generatedQuestions.length}</strong> preguntas de alta calidad. ¿Dónde deseas guardarlo?</p>

                                        <div className="save-actions">
                                            <div className="save-btn-wrapper">
                                                <button
                                                    className="save-account-btn"
                                                    onClick={handleSaveToAccount}
                                                    disabled={generating || isVaultPublishing || isReviewSubmitting}
                                                >
                                                    📂 Guardar en Mis Cuestionarios (Privado)
                                                </button>
                                                {saveProgress > 0 && (
                                                    <div className="mini-progress-bar">
                                                        <div className="mini-progress-fill" style={{ width: `${saveProgress}%` }}></div>
                                                    </div>
                                                )}
                                            </div>

                                            <div className="save-btn-wrapper">
                                                <button
                                                    className="review-submit-btn"
                                                    onClick={handleSendToReview}
                                                    disabled={generating || isVaultPublishing || isReviewSubmitting}
                                                    style={{
                                                        backgroundColor: 'var(--accent, #6200ea)',
                                                        color: 'white',
                                                        padding: '1rem',
                                                        border: 'none',
                                                        borderRadius: '6px',
                                                        cursor: 'pointer',
                                                        fontWeight: 'bold',
                                                        fontFamily: 'var(--font-tactical)',
                                                        fontSize: '0.72rem',
                                                        letterSpacing: '1.5px',
                                                        textTransform: 'uppercase',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        gap: '0.5rem',
                                                        transition: 'all 0.3s'
                                                    }}
                                                >
                                                    📨 Enviar a Revisión de Admin
                                                </button>
                                                {reviewProgress > 0 && (
                                                    <div className="mini-progress-bar review">
                                                        <div className="mini-progress-fill review" style={{ width: `${reviewProgress}%`, height: '100%', background: '#6200ea' }}></div>
                                                    </div>
                                                )}
                                            </div>

                                            <div className="save-btn-wrapper">
                                                <button
                                                    className="publish-vault-btn"
                                                    onClick={handleSaveToVault}
                                                    disabled={generating || isVaultPublishing || isReviewSubmitting}
                                                >
                                                    {isVaultPublishing ? 'Publicando...' : '🛡️ Publicar en Bóveda Técnica (Público)'}
                                                </button>
                                                {publishProgress > 0 && (
                                                    <div className="mini-progress-bar vault">
                                                        <div className="mini-progress-fill vault" style={{ width: `${publishProgress}%` }}></div>
                                                    </div>
                                                )}
                                            </div>

                                            <button
                                                className="reset-btn"
                                                onClick={async () => {
                                                    if (await window.confirmAsync("¿Deseas crear un nuevo cuestionario? Se perderán las preguntas actuales no guardadas.")) {
                                                        setGeneratedQuestions([]);
                                                        setFile(null);
                                                        setExtractedText('');
                                                        setCustomTemaName('');
                                                        setDocAreaId('');
                                                    }
                                                }}
                                                style={{
                                                    backgroundColor: '#6c757d',
                                                    color: 'white',
                                                    padding: '10px 20px',
                                                    border: 'none',
                                                    borderRadius: '8px',
                                                    cursor: 'pointer',
                                                    fontWeight: 'bold',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '8px'
                                                }}
                                            >
                                                🔄 Crear Nuevo Cuestionario
                                            </button>
                                        </div>

                                        <small>
                                            * Los cuestionarios en la Bóveda Técnica son visibles para todos los marineros y se sincronizan con la App Android.
                                        </small>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            )}


            {mode === 'json' && (
                <div className="json-import-container" style={{ animation: 'fadeIn 0.3s ease-out' }}>
                    <div className="welcome-card">
                        <div className="welcome-content">
                            <h1>📂 Importar Cuestionario desde JSON</h1>
                            <p>Carga un archivo de preguntas en formato JSON para agregarlo directamente a tu biblioteca personal (únicamente cargado para ti).</p>
                            
                            {jsonFileContent ? (
                                <div className="json-preview-section" style={{ marginTop: '15px' }}>
                                    <h3 style={{ color: jsonError ? '#f44336' : '#4caf50' }}>📄 Vista Previa del Archivo Cargado:</h3>
                                    <pre style={{ background: 'rgba(0,0,0,0.3)', padding: '15px', borderRadius: '6px', fontSize: '0.75rem', overflowX: 'auto', textAlign: 'left', border: jsonError ? '1px solid rgba(244, 67, 54, 0.5)' : '1px solid rgba(76, 175, 80, 0.5)', maxHeight: '250px', overflowY: 'auto' }}>
                                        {jsonFileContent.length > 2000 ? jsonFileContent.substring(0, 2000) + '\n\n... [Texto truncado por longitud]' : jsonFileContent}
                                    </pre>
                                    {jsonError && (
                                        <div style={{ marginTop: '10px', color: '#f44336', background: 'rgba(244, 67, 54, 0.1)', padding: '10px', borderRadius: '4px', borderLeft: '3px solid #f44336', textAlign: 'left' }}>
                                            <strong style={{ display: 'block', marginBottom: '5px' }}>❌ Error detectado en tu archivo:</strong>
                                            <p style={{ margin: 0, fontSize: '0.85rem' }}>{jsonError}</p>
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <div>
                                    <h3>💡 Estructura Esperada del JSON (Múltiples formatos soportados):</h3>
                                    <pre style={{ background: 'rgba(0,0,0,0.3)', padding: '15px', borderRadius: '6px', fontSize: '0.75rem', overflowX: 'auto', textAlign: 'left', border: '1px solid rgba(255,255,255,0.1)' }}>
{`[
  {
    "pregunta": "¿Cuál es la capital de México?",
    "respuestas": [
      { "respuesta": "Monterrey", "correcta": false },
      { "respuesta": "Guadalajara", "correcta": false },
      { "respuesta": "CDMX", "correcta": true },
      { "respuesta": "Cancún", "correcta": false }
    ]
  }
]`}
                                    </pre>
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="file-upload-section">
                        <div
                            className={`drop-zone ${isJsonProcessing ? 'processing' : ''}`}
                            onDragOver={(e) => e.preventDefault()}
                            onDrop={(e) => {
                                e.preventDefault();
                                const droppedFile = e.dataTransfer.files[0];
                                if (droppedFile) handleJsonFileSelect(droppedFile);
                            }}
                        >
                            <input
                                type="file"
                                id="json-file-upload"
                                accept=".json"
                                onChange={(e) => handleJsonFileSelect(e.target.files[0])}
                                style={{ display: 'none' }}
                            />
                            <label htmlFor="json-file-upload" className="upload-label">
                                {jsonFile ? (
                                    <div className="file-info">
                                        <span className="file-icon">📁</span>
                                        <span className="file-name">{jsonFile.name}</span>
                                        <span className="change-file">(Clic para cambiar)</span>
                                    </div>
                                ) : (
                                    <div className="upload-placeholder">
                                        <span className="upload-icon"> ☁️ Da click aquí para agregar el archivo JSON ☁️ </span>
                                        <p>Arrastra tu archivo JSON aquí o haz clic para seleccionar</p>
                                        <small>Solo archivos .json</small>
                                    </div>
                                )}
                            </label>
                        </div>

                        {isJsonProcessing && (
                            <div className="processing-indicator">
                                <div className="spinner"></div>
                                <p>Procesando archivo JSON...</p>
                            </div>
                        )}

                        {/* {jsonError && (
                            <div className="status-message error">
                                ❌ {jsonError}
                            </div>
                        )} */}

                        {jsonSuccess && (
                            <div className="status-message success">
                                ✅ {jsonSuccess}
                            </div>
                        )}

                        {jsonParsedQuiz && (
                            <div className="json-save-section" style={{ marginTop: '20px', padding: '20px', background: 'rgba(10, 26, 42, 0.8)', borderRadius: '8px', border: '1px solid var(--tech-border)' }}>
                                <h3 style={{ color: 'var(--gold-matte)', fontFamily: 'var(--font-tactical)', fontSize: '0.9rem', marginBottom: '15px' }}>CONFIGURACIÓN DEL CUESTIONARIO IMPORTADO</h3>
                                
                                <div className="form-group">
                                    <label>Área de Organización:</label>
                                    <select
                                        value={jsonAreaId}
                                        onChange={(e) => setJsonAreaId(e.target.value)}
                                        style={{ padding: '8px', borderRadius: '4px', border: '1px solid var(--tech-border)', width: '100%', color: 'white', background: 'rgba(0,0,0,0.5)' }}
                                    >
                                        <option value="">Selecciona un Área</option>
                                        {areas.map(area => (
                                            <option key={area.id} value={area.id}>{area.nombre}</option>
                                        ))}
                                    </select>
                                    <small style={{ color: 'var(--text-muted)', display: 'block', marginTop: '5px' }}>
                                        Selecciona en qué sección de tu biblioteca quieres organizar este cuestionario.
                                    </small>
                                </div>

                                <button
                                    className="generate-button"
                                    onClick={handleSaveJsonQuiz}
                                    disabled={!jsonAreaId || isJsonProcessing}
                                    style={{ marginTop: '15px', width: '100%', display: 'block' }}
                                >
                                    💾 Guardar como Cuestionario Personal (Privado)
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default QuizGenerator;